import { useEffect, useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';
import { AxiosError } from 'axios';

import { getSellerProfileReq } from '../api/sale.requests';
import type { SellerProfileResponse } from '../types/sale.types';

interface SellerProfileCacheState {
  profile: SellerProfileResponse | null;
  error: Error | null;
  isNotFound: boolean;
  isResolved: boolean;
}

interface UseSellerProfileReturn {
  profile: SellerProfileResponse | null;
  isLoading: boolean;
  error: Error | null;
  isNotFound: boolean;
}

const EMPTY_SELLER_PROFILE_CACHE: SellerProfileCacheState = {
  profile: null,
  error: null,
  isNotFound: false,
  isResolved: true,
};

const INITIAL_SELLER_PROFILE_CACHE: SellerProfileCacheState = {
  profile: null,
  error: null,
  isNotFound: false,
  isResolved: false,
};

let sellerProfileCache: SellerProfileCacheState = EMPTY_SELLER_PROFILE_CACHE;
let cachedUserId: string | null = null;
let sellerProfileFetchPromise: Promise<SellerProfileCacheState> | null = null;
let activeFetchUserId: string | null = null;

export const isSellerSalesAccessGranted = (profile: SellerProfileResponse | null): boolean =>
  Boolean(profile?.isVerifiedSeller && profile.status === 'completed');

/** True when contacts (stage 1) are done and full package (stage 2) is still pending */
export const isSellerApplicationStageTwoPending = (
  profile: SellerProfileResponse | null | undefined,
): boolean => profile?.applicationStage === 1;

export const invalidateSellerProfileCache = (): void => {
  sellerProfileCache = EMPTY_SELLER_PROFILE_CACHE;
  cachedUserId = null;
  sellerProfileFetchPromise = null;
  activeFetchUserId = null;
};

const resolveUserId = (userId: string | number | null | undefined): string | null => {
  if (userId == null) {
    return null;
  }

  return String(userId);
};

const getCacheStateForUser = (
  userId: string | null,
  sessionStatus: string,
): SellerProfileCacheState => {
  if (sessionStatus === 'loading') {
    if (userId && cachedUserId === userId && sellerProfileCache.isResolved) {
      return sellerProfileCache;
    }

    return INITIAL_SELLER_PROFILE_CACHE;
  }

  if (!userId) {
    return EMPTY_SELLER_PROFILE_CACHE;
  }

  if (cachedUserId === userId && sellerProfileCache.isResolved) {
    return sellerProfileCache;
  }

  return INITIAL_SELLER_PROFILE_CACHE;
};

const fetchSellerProfile = async (userId: string): Promise<SellerProfileCacheState> => {
  if (sellerProfileCache.isResolved && cachedUserId === userId) {
    return sellerProfileCache;
  }

  if (sellerProfileFetchPromise && cachedUserId === userId) {
    return sellerProfileFetchPromise;
  }

  sellerProfileFetchPromise = (async () => {
    activeFetchUserId = userId;

    try {
      const data = await getSellerProfileReq();

      if (activeFetchUserId !== userId) {
        return sellerProfileCache;
      }

      sellerProfileCache = {
        profile: data,
        error: null,
        isNotFound: false,
        isResolved: true,
      };
    } catch (err) {
      if (activeFetchUserId !== userId) {
        return sellerProfileCache;
      }

      const axiosError = err as AxiosError;

      if (axiosError?.response?.status === 404) {
        sellerProfileCache = {
          profile: null,
          error: null,
          isNotFound: true,
          isResolved: true,
        };
      } else {
        sellerProfileCache = {
          profile: null,
          error:
            axiosError instanceof Error ? axiosError : new Error('Failed to fetch seller profile'),
          isNotFound: false,
          isResolved: true,
        };
      }
    }

    cachedUserId = userId;
    return sellerProfileCache;
  })();

  try {
    return await sellerProfileFetchPromise;
  } finally {
    sellerProfileFetchPromise = null;
  }
};

export const useSellerProfileQuery = (): UseSellerProfileReturn => {
  const { data: session, status } = useSession();
  const userId = resolveUserId(session?.user?.id);
  const [fetchVersion, setFetchVersion] = useState(0);

  const cacheState = useMemo(
    () => getCacheStateForUser(userId, status),
    [fetchVersion, status, userId],
  );

  useEffect(() => {
    if (status === 'loading') {
      return;
    }

    if (!userId) {
      invalidateSellerProfileCache();
      return;
    }

    if (cachedUserId !== userId) {
      invalidateSellerProfileCache();
    }

    if (sellerProfileCache.isResolved && cachedUserId === userId) {
      return;
    }

    let isMounted = true;

    fetchSellerProfile(userId).then(() => {
      if (isMounted) {
        setFetchVersion((version) => version + 1);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [status, userId]);

  return {
    profile: cacheState.profile,
    isLoading: !cacheState.isResolved,
    error: cacheState.error,
    isNotFound: cacheState.isNotFound,
  };
};
